import { logToExternalErrorHandlers } from '@luminovo/commons';
import { httpQuery } from '@luminovo/data-stack';
import { stringSearch } from '@luminovo/design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';

import DigikeyRedirectPage from '@/areas/supplier-integrations/SupplierApiIntegrations/DigikeyRedirectPage';
import { canOpenSupplierIntegrations } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// The loader trades DigiKey's `code` for its token and returns to the supplier integrations, so the page renders only
// when connecting failed.
export const Route = createFileRoute('/_authenticated/3rdparty/digikey/callback')({
    // What the Supplier integrations page and its layout check: only it connects DigiKey, and the user returns there.
    staticData: {
        access: { permissions: canOpenSupplierIntegrations },
        chrome: 'none',
    } satisfies PageStaticData,
    validateSearch: stringSearch({ code: 'optional', error: 'optional', error_description: 'optional' }),
    // The exchange uses the code up. Returning early on the loader's `preload` would not do: a click that lands while
    // the hover's run is still going gets that run's result.
    preload: false,
    loaderDeps: ({ search }) => ({ code: search.code }),
    loader: async ({ context: { queryClient }, deps: { code } }) => {
        // The user declined consent.
        if (code === undefined) {
            return;
        }
        // A failed exchange shows the page, not the error component, so the loader reports it: after DigiKey's full
        // page load it can fail before `GlobalQueryErrorHandler` renders, which starts the query cache's reporting.
        // If both report, Sentry drops the second capture.
        const exchanged = await queryClient
            .ensureQueryData(
                httpQuery('GET /3rdparty/digikey/acquire-access-token-from-code', { queryParams: { auth_code: code } }),
            )
            .then(
                () => true,
                (error: unknown) => {
                    logToExternalErrorHandlers(error instanceof Error ? error : new Error(String(error)));
                    return false;
                },
            );
        if (exchanged) {
            throw redirect({ to: '/settings/organization/supplier-api-integrations', replace: true });
        }
    },
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const code = Route.useSearch({ select: (search) => search.code });
    const error = Route.useSearch({ select: (search) => search.error });
    const errorDescription = Route.useSearch({ select: (search) => search.error_description });
    return <DigikeyRedirectPage code={code} error={error} errorDescription={errorDescription} />;
}
